/**
 * Payment API Service
 * Service layer for vendor payment data management API calls
 *
 * @see api-spec.yaml - Vendor Payment section
 */

import apiClient from './axios.config';
import { VENDOR_ROUTES } from '@/constants/api.constants';
import type {
  VendorPaymentDataResponse,
  CreateVendorPaymentRequest,
  UpdateVendorPaymentRequest,
} from '@/types/api/paymentTerm';

/**
 * Payment API service object
 * Uses types from paymentTerm.ts which match the API spec
 */
export const paymentApi = {
  /**
   * GET /api/vendors/:vendorId/payment
   * Fetch payment data for a vendor
   */
  get: async (vendorId: number): Promise<VendorPaymentDataResponse> => {
    const response = await apiClient.get<VendorPaymentDataResponse>(
      VENDOR_ROUTES.PAYMENT(vendorId)
    );
    return response.data;
  },

  /**
   * POST /api/vendors/:vendorId/payment
   * Create payment data for a vendor
   *
   * @see VendorPaymentCreate schema in api-spec.yaml
   */
  create: async (
    vendorId: number,
    data: Omit<CreateVendorPaymentRequest, 'vendorId'>
  ): Promise<VendorPaymentDataResponse> => {
    // Build request matching VendorPaymentCreate schema
    const requestData: CreateVendorPaymentRequest = {
      vendorId,
      basePaymentTermId: data.basePaymentTermId,
      primaryCurrency: data.primaryCurrency,
      taxTypeId: data.taxTypeId,
      // Deposit and Cash Discount percentages
      ...(data.depositPercentage !== undefined && { depositPercentage: data.depositPercentage }),
      ...(data.cashDiscountPercentage !== undefined && { cashDiscountPercentage: data.cashDiscountPercentage }),
      // Optional custom term fields
      ...(data.customTermName && { customTermName: data.customTermName }),
      ...(data.customDueDateBase && { customDueDateBase: data.customDueDateBase }),
      ...(data.customDueDays !== undefined && { customDueDays: data.customDueDays }),
      ...(data.customHasDiscount !== undefined && { customHasDiscount: data.customHasDiscount }),
      ...(data.customDiscountRate !== undefined && { customDiscountRate: data.customDiscountRate }),
      ...(data.customDiscountDays !== undefined && { customDiscountDays: data.customDiscountDays }),
      ...(data.customDescription && { customDescription: data.customDescription }),
    };

    const response = await apiClient.post<VendorPaymentDataResponse>(
      VENDOR_ROUTES.PAYMENT(vendorId),
      requestData
    );
    return response.data;
  },

  /**
   * PUT /api/vendors/:vendorId/payment
   * Update payment data for a vendor
   *
   * @see VendorPaymentUpdate schema in api-spec.yaml
   */
  update: async (
    vendorId: number,
    data: UpdateVendorPaymentRequest
  ): Promise<VendorPaymentDataResponse> => {
    const response = await apiClient.put<VendorPaymentDataResponse>(
      VENDOR_ROUTES.PAYMENT(vendorId),
      data
    );
    return response.data;
  },
};

export default paymentApi;
